// NodePeekContext.js

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { shouldShowTypeViewerTab } from 'node/highlight/nodeTypeViewerRegistry';
import {
  discardIfProvisional,
  getProvisionalSession,
} from 'node/provisional/provisionalNodeSession';
import { UNIFIED_TAB_KEYS, getSessionTabKey } from 'node/tabs/unifiedNodeTabKeys';

const NodePeekContext = createContext(null);
const PEEK_HISTORY_LIMIT = 30;
const DEFAULT_PEEK_PANEL_TAB_KEY = UNIFIED_TAB_KEYS.ABOUT;

const resolvePeekTabForNode = (nodeKey, node = null) => {
  const sessionTabKey = getSessionTabKey(nodeKey);
  if (sessionTabKey) return sessionTabKey;
  if (shouldShowTypeViewerTab(node?.type, { surface: 'peek' })) {
    return UNIFIED_TAB_KEYS.HIGHLIGHT;
  }
  return DEFAULT_PEEK_PANEL_TAB_KEY;
};

/**
 * True when the node being left is still an uncommitted provisional stub.
 *
 * @param {string} nodeKey
 * @param {object|null|undefined} seedNode
 * @returns {boolean}
 */
const isLeavingProvisional = (nodeKey, seedNode) => {
  const key = typeof nodeKey === 'string' ? nodeKey.trim() : '';
  if (!key) return false;

  const session = getProvisionalSession(key);
  if (session?.committed) return false;
  if (session) return true;

  return seedNode?.key === key && seedNode?.provisional === true;
};

export const NodePeekProvider = ({ children }) => {
  // ----- STATE ----- //

  const [peekNodeKey, setPeekNodeKey] = useState('');
  const [peekSeedNode, setPeekSeedNode] = useState(null);
  const [isPeekOpen, setIsPeekOpen] = useState(false);
  const [peekPanelTabKey, setPeekPanelTabKey] = useState(DEFAULT_PEEK_PANEL_TAB_KEY);
  const [peekHistoryStack, setPeekHistoryStack] = useState([]);

  // ----- HANDLERS ----- //

  const openPeek = useCallback((node, options = {}) => {
    const key = typeof node?.key === 'string' ? node.key.trim() : '';
    if (!key) return;

    const mode = options?.mode === 'push' ? 'push' : 'reset';
    const currentKey = typeof peekNodeKey === 'string' ? peekNodeKey.trim() : '';
    const leavingProvisional = Boolean(
      currentKey
      && currentKey !== key
      && isLeavingProvisional(currentKey, peekSeedNode),
    );

    if (leavingProvisional) {
      void discardIfProvisional(
        currentKey,
        peekSeedNode?.key === currentKey ? peekSeedNode : null,
      );
    }

    setPeekHistoryStack((prev) => {
      if (mode !== 'push') return [];
      if (!currentKey || currentKey === key) return prev;
      // Do not stack a stub we are discarding.
      if (leavingProvisional) return prev;

      const currentEntry = {
        key: currentKey,
        seedNode: peekSeedNode?.key === currentKey ? peekSeedNode : null,
      };

      const lastEntry = prev[prev.length - 1] || null;
      if (lastEntry?.key === currentEntry.key) return prev;

      const next = [...prev, currentEntry];
      if (next.length <= PEEK_HISTORY_LIMIT) return next;
      return next.slice(next.length - PEEK_HISTORY_LIMIT);
    });

    setPeekSeedNode(node || null);
    setPeekNodeKey(key);
    setPeekPanelTabKey(resolvePeekTabForNode(key, node));
    setIsPeekOpen(true);
  }, [peekNodeKey, peekSeedNode]);

  const goBackPeek = useCallback(() => {
    const leavingKey = typeof peekNodeKey === 'string' ? peekNodeKey.trim() : '';
    const leavingSeed = peekSeedNode;

    let previousEntry = null;

    setPeekHistoryStack((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      previousEntry = prev[prev.length - 1] || null;
      return prev.slice(0, -1);
    });

    const key = typeof previousEntry?.key === 'string' ? previousEntry.key.trim() : '';
    if (!key) return;

    if (leavingKey && leavingKey !== key) {
      void discardIfProvisional(leavingKey, leavingSeed);
    }

    const seedNode = previousEntry?.seedNode || null;
    setPeekSeedNode(seedNode);
    setPeekNodeKey(key);
    setPeekPanelTabKey(resolvePeekTabForNode(key, seedNode));
    setIsPeekOpen(true);
  }, [peekNodeKey, peekSeedNode]);

  const closePeek = useCallback(() => {
    const key = typeof peekNodeKey === 'string' ? peekNodeKey.trim() : '';
    if (key) {
      void discardIfProvisional(key, peekSeedNode);
    }
    // Keep the last tab in state/session — resetting to About here was
    // persisting About over the remembered tab while the panel unmounted.
    setIsPeekOpen(false);
  }, [peekNodeKey, peekSeedNode]);

  const clearPeek = useCallback(() => {
    const key = typeof peekNodeKey === 'string' ? peekNodeKey.trim() : '';
    if (key) {
      void discardIfProvisional(key, peekSeedNode);
    }
    setPeekHistoryStack([]);
    setPeekSeedNode(null);
    setPeekNodeKey('');
    setIsPeekOpen(false);
  }, [peekNodeKey, peekSeedNode]);

  // ----- MEMOS ----- //

  const value = useMemo(() => ({
    isPeekOpen,
    peekNodeKey,
    peekSeedNode,
    peekPanelTabKey,
    canPeekGoBack: peekHistoryStack.length > 0,
    openPeek,
    goBackPeek,
    closePeek,
    clearPeek,
    setPeekPanelTabKey,
  }), [
    isPeekOpen,
    peekNodeKey,
    peekSeedNode,
    peekPanelTabKey,
    peekHistoryStack.length,
    openPeek,
    goBackPeek,
    closePeek,
    clearPeek,
    setPeekPanelTabKey,
  ]);

  return (
    <NodePeekContext.Provider value={value}>
      {children}
    </NodePeekContext.Provider>
  );
};

export const useNodePeek = () => {
  const context = useContext(NodePeekContext);
  if (!context) {
    throw new Error('useNodePeek must be used within NodePeekProvider');
  }
  return context;
};

/**
 * Same as useNodePeek but returns null outside the provider (e.g. Workspace rail).
 *
 * @returns {object|null}
 */
export const useOptionalNodePeek = () => useContext(NodePeekContext);

export { UNIFIED_TAB_KEYS };

export default NodePeekContext;
